<template>
  <div class="add-shop">
    <div class="add-shop__item" v-for="(item, index) of value" :key="index">
      <div class="add-shop__delete">
        <v-button :disabled="disabled" class="add-shop__delete-btn" rounded color="error" @click="remove(index)" size="small">
          <span class="icon-trash text--x-large"></span>
        </v-button>
      </div>

      <v-input :disabled="disabled" :placeholder="$t('components.Profile.addShop.name')" @input="(e) => onInputShopName(index, e)" :value="item.name"></v-input>

      <add-shop-map :value="item" @input="(e) => onInputMap(index, e)" class="my-1">
        <template v-slot:activator="{ on }">
          <v-button :disabled="disabled" width="full" @click="on" color="space" v-if="!item.address"><span class="icon-mark"></span> {{ $t('components.Profile.addShop.address') }}</v-button>
          <div class="add-shop__address-container" v-else>
            <v-button inline :disabled="disabled" @click="on" class="add-shop__address-edit d-block"><i class="icon-edit"></i></v-button>
            <div class="text-color-purple-black text--bold">{{ $t('components.Profile.addShop.address') }}</div>
            <div class="text-color-grey-dark mt-05">{{ item.address }}</div>
          </div>
        </template>
      </add-shop-map>

      <add-shop-contact-list class="my-1" @input="(e) => onAddShopContactList(index, e)" :value="item.contactList">
        <template v-slot:activator="{ on }">
          <v-button :disabled="disabled" width="full" @click="on" color="space" v-if="!item.contactList || item.contactList.length === 0"><span class="icon-users"></span> {{ $t('components.Profile.addShop.addContracts') }}</v-button>
          <div class="add-shop__contacts-container" v-else>
            <v-button inline :disabled="disabled" @click="on" class="add-shop__address-edit d-block"><i class="icon-edit"></i></v-button>
            <div class="add-shop__contacts-item-title text-color-purple-black text--bold">
              {{ $t('components.Profile.addShop.contactList') }}
              <div class="add-shop__contacts-item-badge">
                <div>{{ item.contactList.length > 9 ? '9+' : item.contactList.length }}</div>
              </div>
            </div>
            <div class="add-shop__contacts-item py-1">
              <div v-for="(contact, index) of item.contactList" :key="index + '-' + contact.id" class="mb-05">
                <div class="text-color-grey-dark" v-if="index < 2">— {{ contactTypes[contact.type] }}: {{ contact.name }}{{ contact.name && contact.contact ? ', ' : '' }}{{ contact.contact }}</div>
                <div class="text-color-grey-dark" v-else-if="index === 2">...</div>
              </div>
            </div>
          </div>
        </template>
      </add-shop-contact-list>

      <add-shop-working-hours class="my-1" @input="(e) => onAddWorkingHours(index, e)" :value="item.workingHoursList">
        <template v-slot:activator="{ on }">
          <v-button :disabled="disabled" width="full" @click="on" color="space" v-if="!item.workingHoursList || item.workingHoursList.length === 0"><span class="icon-calendar"></span> {{ $t('components.Profile.addShop.workingHours') }}</v-button>
          <div class="add-shop__hours-container" v-else>
            <v-button inline :disabled="disabled" @click="on" class="add-shop__address-edit d-block"><i class="icon-edit"></i></v-button>
            <div class="add-shop__hours-item-title text-color-purple-black text--bold">
              {{ $t('components.Profile.addShop.workingHours') }}
              <div class="add-shop__hours-item-badge">
                <div>{{ item.workingHoursList.length > 9 ? '9+' : item.workingHoursList.length }}</div>
              </div>
            </div>
            <div class="add-shop__hours-item py-1">
              <div v-for="(hour, index) of item.workingHoursList" :key="index + '-' + 'workingHour'" class="mb-05">
                <div class="text-color-grey-dark" v-if="hour.weekDay && index < 2">— {{ weekDays[hour.weekDay] }}: {{ getTime(hour.workStartMin) }}{{ hour.workEndMin !== null ? ' - ' : '' }}{{ getTime(hour.workEndMin) }}</div>
                <div class="text-color-grey-dark" v-else-if="hour.date && index < 2 ">— {{ toSimpleDate(hour.date, true) }}: {{ hour.workStartMin !== null ? getTime(hour.workStartMin) : '' }}{{ hour.workEndMin !== null ? ' - ' : '' }}{{ hour.workEndMin !== null ? getTime(hour.workEndMin) : '' }}</div>
                <div class="text-color-grey-dark" v-else-if="index === 2">...</div>
              </div>
            </div>
          </div>
        </template>
      </add-shop-working-hours>

      <div class="add-shop__error text-color-error mt-1" v-if="error && Array.isArray(error)">{{error[index]}}</div>
    </div>

    <div class="add-shop__add d-flex justify-center">
      <v-button :disabled="disabled" class="add-shop__add-btn" size="large" outline rounded @click="add()" color="secondary">
        <span class="icon-plus text--xx-large"></span>
      </v-button>
    </div>

    <div class="add-shop__error text-color-error" v-if="error && !Array.isArray(error)">{{error}}</div>
  </div>
</template>

<script>
import vInput from '@/ui/controls/vInput'
import vButton from '@/ui/controls/vButton'

import addShopWorkingHours from '@/components/Profile/addShopWorkingHours'

import toHoursMinutesDate from '@/mixins/toHoursMinutesDate'
import toSimpleDate from '@/mixins/toSimpleDate'

import addShopMap from './addShopMap'
import addShopContactList from './addShopContactList'

export default {
  name: 'Modals-mapShop',

  mixins: [toHoursMinutesDate, toSimpleDate],

  components: {
    vInput,
    vButton,

    addShopWorkingHours,
    addShopMap,
    addShopContactList
  },

  props: {
    value: {
      type: Array,
      default: () => []
    },
    error: {
      type: [String, Array],
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    weekDays () {
      const out = {}
      const arr = [
        {
          label: this.$t('general.weekDays.nominative.Sunday'),
          value: 7
        },
        {
          label: this.$t('general.weekDays.nominative.Monday'),
          value: 1
        },
        {
          label: this.$t('general.weekDays.nominative.Tuesday'),
          value: 2
        },
        {
          label: this.$t('general.weekDays.nominative.Wednesday'),
          value: 3
        },
        {
          label: this.$t('general.weekDays.nominative.Thursday'),
          value: 4
        },
        {
          label: this.$t('general.weekDays.nominative.Friday'),
          value: 5
        },
        {
          label: this.$t('general.weekDays.nominative.Saturday'),
          value: 6
        }
      ]

      for (const i of arr) {
        out[i.value] = i.label
      }

      return out
    },

    contactTypes () {
      const out = {}
      const arr = [
        {
          value: 'phone',
          name: this.$t('components.Profile.addShop.contactListPopup.phone')
        },
        {
          value: 'email',
          name: this.$t('components.Profile.addShop.contactListPopup.email')
        },
        {
          value: 'whatsapp',
          name: this.$t('components.Profile.addShop.contactListPopup.whatsup')
        },
        {
          value: 'telegram',
          name: this.$t('components.Profile.addShop.contactListPopup.telegram')
        },
        {
          value: 'viber',
          name: this.$t('components.Profile.addShop.contactListPopup.viber')
        },
        {
          value: 'skype',
          name: this.$t('components.Profile.addShop.contactListPopup.skype')
        }
      ]

      for (const i of arr) {
        out[i.value] = i.name
      }

      return out
    }
  },

  methods: {
    getWeekDayValue (value) {
      const obj = this.weekDays.find(val => parseInt(value) === val.value)
      if (obj) {
        return obj.label
      }
    },

    getTime (val) {
      if (val === null) {
        return val
      }
      const date = new Date()
      const hours = Math.trunc(val / 60)
      const minutes = val % 60
      date.setHours(hours)
      date.setMinutes(minutes)
      date.setSeconds(0)
      date.setMilliseconds(0)
      return this.toHoursMinutesDate(date)
    },

    remove (index) {
      const items = this.value
      items.splice(index, 1)
      this.$emit('input', items)
    },

    add () {
      const items = this.value
      items.push({
        name: null,
        address: null,
        lat: null,
        lng: null
      })
      this.$emit('input', items)
    },

    onAddWorkingHours (index, e) {
      const items = [...this.value]
      items[index].workingHoursList = e
      this.$emit('input', items)
    },

    onAddShopContactList (index, e) {
      const items = this.value
      items[index].contactList = e
      this.$emit('input', items)
    },

    onInputShopName (index, e) {
      const items = this.value
      if (e === '') {
        items[index].name = null
      } else {
        items[index].name = e
      }
      this.$emit('input', items)
    },

    onInputMap (index, e) {
      const items = this.value
      items[index].address = e.address
      items[index].lat = e.lat
      items[index].lng = e.lng
      this.$emit('input', items)
    },

    onInputWorkingHoursList (index, e) {
      const items = this.value
      items[index].workingHoursList = e
      this.$emit('input', items)
    },

    contactList (index, e) {
      const items = this.value
      items[index].contactList = e
    }
  }
}
</script>

<style lang="scss">
  .add-shop {
    &__item {
      border: 2px dashed $color-azure;
      padding: 2.5rem 1.5rem 2rem 1.5rem;
      border-radius: $border-radius;

      position: relative;

      &:not(:last-child) {
        margin-bottom: 2rem;
      }
    }

    &__delete {
      position: absolute;
      transform: translate(-50%, -50%);
      left: 50%;
      top: 0;
    }

    &__address-container, &__contacts-container, &__hours-container {
      position: relative;
      margin-top: 1.5rem;
      background-color: $color-grey-extra-light;
      padding: .8rem 1rem;
      border-radius: $border-radius;
    }

    &__contacts-item-title, &__hours-item-title {
      position: relative;
      display: inline-block;
    }

    &__contacts-item-badge, &__hours-item-badge {
      position: absolute;
      top: -.7rem;
      right: -1.8rem;
      font-size: 1rem;
      width: 1.7rem;
      height: 1.7rem;
      color: $color-white;
      background-color: $color-green;
      border-radius: 50%;

      & > * {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-55%, -50%);
      }
    }

    &__address-edit {
      position: absolute;
      top: .5rem;
      right: 1rem;
      color: $color-secondary;
      font-size: 2rem;

      &:link, &:hover, &:active, &:visited {
        color: $color-secondary;
        font-size: 2rem;
      }
    }
  }
</style>
